import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import type { FailureBreakdown } from "@/lib/analytics/types";

interface FailureAnalysisProps {
  breakdown: FailureBreakdown;
}

const COLORS: Record<string, string> = {
  success: "var(--chart-2)",
  failure: "var(--destructive)",
  cancelled: "var(--chart-3)",
  timed_out: "var(--chart-4)",
  startup_failure: "var(--chart-5)",
  action_required: "var(--chart-1)",
  neutral: "var(--muted-foreground)",
  skipped: "var(--muted)",
};

export function FailureAnalysis({ breakdown }: FailureAnalysisProps) {
  const sliceData = breakdown.slices.map((s) => ({ name: s.conclusion, value: s.count }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Failure analysis</CardTitle>
        <CardDescription>Breakdown by conclusion + top failing workflows</CardDescription>
      </CardHeader>
      <CardContent className="grid md:grid-cols-2 gap-6">
        <div className="h-64">
          {sliceData.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
              No finished runs.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={sliceData} dataKey="value" nameKey="name" innerRadius={40} outerRadius={85}>
                  {sliceData.map((d) => (
                    <Cell key={d.name} fill={COLORS[d.name] ?? "var(--chart-3)"} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: "var(--popover)",
                    border: "1px solid var(--border)",
                    borderRadius: "0.375rem",
                    fontSize: "0.75rem",
                  }}
                />
                <Legend wrapperStyle={{ fontSize: "0.75rem" }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
        <div>
          <h4 className="text-sm font-medium mb-2">Top failing workflows</h4>
          {breakdown.topFailingWorkflows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No failing workflows.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {breakdown.topFailingWorkflows.map((wf) => (
                <li key={wf.workflowId} className="flex items-center justify-between gap-3">
                  <span className="truncate">{wf.name}</span>
                  <div className="flex items-center gap-2">
                    <Badge variant="destructive">
                      {wf.failures} / {wf.total}
                    </Badge>
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {(wf.failureRate * 100).toFixed(0)}%
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
